
import { useRef, useEffect, useState } from 'react';

export default function Packs() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  const packs = [
    {
      title: 'Spicy Nights',
      subtitle: '18+ only',
      icon: 'ri-fire-fill',
      gradient: 'from-red-600 to-orange-600',
    },
    {
      title: 'Besties & Secrets',
      subtitle: 'Close friends',
      icon: 'ri-heart-line',
      gradient: 'from-pink-600 to-rose-600',
    },
    {
      title: 'Couples Edition',
      subtitle: 'Date night',
      icon: 'ri-hearts-fill',
      gradient: 'from-rose-600 to-pink-600',
    },
    {
      title: 'On Tour',
      subtitle: 'Road trips',
      icon: 'ri-roadster-line',
      gradient: 'from-cyan-600 to-teal-600',
    },
    {
      title: 'Office Party',
      subtitle: 'Work friends',
      icon: 'ri-briefcase-line',
      gradient: 'from-amber-600 to-yellow-600',
    },
    {
      title: 'Family Friendly',
      subtitle: 'All ages',
      icon: 'ri-home-heart-line',
      gradient: 'from-emerald-600 to-green-600',
    },
    {
      title: 'Late Night',
      subtitle: 'After hours',
      icon: 'ri-moon-fill',
      gradient: 'from-indigo-600 to-violet-600',
    },
    {
      title: 'Festival Mode',
      subtitle: 'Big crowds',
      icon: 'ri-music-2-fill',
      gradient: 'from-fuchsia-600 to-pink-600',
    },
    {
      title: 'Beach Vibes',
      subtitle: 'Summer fun',
      icon: 'ri-sun-fill',
      gradient: 'from-orange-500 to-amber-500',
    },
    {
      title: 'Game Night',
      subtitle: 'Competitive',
      icon: 'ri-gamepad-line',
      gradient: 'from-teal-600 to-cyan-600',
    },
    {
      title: 'Reunion',
      subtitle: 'Old friends',
      icon: 'ri-group-line',
      gradient: 'from-violet-600 to-purple-600',
    },
    {
      title: 'First Date',
      subtitle: 'Getting to know',
      icon: 'ri-cup-line',
      gradient: 'from-rose-500 to-red-500',
    },
  ];

  // Duplicate packs for infinite scroll effect
  const duplicatedPacks = [...packs, ...packs, ...packs];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || !isAutoScrolling) return;

    let animationId: number;
    let scrollPosition = scrollContainer.scrollLeft;
    const scrollSpeed = 0.5;

    const animate = () => {
      if (!scrollContainer) return;
      
      scrollPosition += scrollSpeed;
      
      // Reset scroll position when reaching the middle set
      const singleSetWidth = scrollContainer.scrollWidth / 3;
      if (scrollPosition >= singleSetWidth * 2) {
        scrollPosition = singleSetWidth;
      }
      
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };

    // Start from middle set
    scrollContainer.scrollLeft = scrollContainer.scrollWidth / 3;
    scrollPosition = scrollContainer.scrollLeft;
    
    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isAutoScrolling]);

  const handleMouseEnter = () => setIsAutoScrolling(false);
  const handleMouseLeave = () => setIsAutoScrolling(true);

  return (
    <section id="packs" className="py-24 bg-gradient-to-b from-transparent to-[#0a0a0f]">
      <div className="max-w-6xl mx-auto px-6 mb-12">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-6 text-[#EFEE40]" style={{ lineHeight: '1.3' }}>
          The fun never stops
        </h2>

        <p className="text-lg text-gray-300 text-center max-w-2xl mx-auto">
          Core games. Expanding packs. Constant updates.
        </p>
      </div>

      {/* Carousel Container */}
      <div className="relative">
        {/* Left fade */}
        <div className="absolute left-0 top-0 bottom-0 w-32 md:w-48 bg-gradient-to-r from-[#0a0a0f] to-transparent z-10 pointer-events-none"></div>
        
        {/* Right fade */}
        <div className="absolute right-0 top-0 bottom-0 w-32 md:w-48 bg-gradient-to-l from-[#0a0a0f] to-transparent z-10 pointer-events-none"></div>

        {/* Scrolling container */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-hidden py-4 px-8"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {[false, true, true].map((hidden, groupIndex) => (
            <div key={groupIndex} className="contents" aria-hidden={hidden || undefined}>
              {packs.map((pack, index) => (
                <div
                  key={index}
                  className={`flex-shrink-0 w-40 h-52 bg-gradient-to-br ${pack.gradient} rounded-2xl p-5 flex flex-col items-center justify-center text-center transition-transform hover:scale-105 cursor-pointer shadow-lg`}
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl mb-4">
                    <i className={`${pack.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-base font-bold text-white mb-1 leading-tight">{pack.title}</h3>
                  <p className="text-xs text-white/80">{pack.subtitle}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom message */}
      <div className="max-w-6xl mx-auto px-6 mt-12">
        <div className="bg-gradient-to-r from-pink-900/20 to-rose-900/20 backdrop-blur-sm border border-pink-500/20 rounded-2xl p-6 text-center">
          <p className="text-gray-300">
            <strong>One-time purchases. No subscriptions. No ads.</strong>
          </p>
        </div>
      </div>
    </section>
  );
}
